import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { tAuto } from "@/lib/tAuto";
interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  totalItems?: number;
  itemsPerPage?: number;
}

export function Pagination({ currentPage, totalPages, onPageChange, totalItems, itemsPerPage }: PaginationProps) {
  if (totalPages <= 1) return null;

  const pages: (number | "...")[] = [];
  const push = (v: number | "...") => { if (pages[pages.length - 1] !== v) pages.push(v); };

  push(1);
  if (currentPage > 3) push("...");
  for (let p = Math.max(2, currentPage - 1); p <= Math.min(totalPages - 1, currentPage + 1); p++) push(p);
  if (currentPage < totalPages - 2) push("...");
  if (totalPages > 1) push(totalPages);

  const from = totalItems && itemsPerPage ? (currentPage - 1) * itemsPerPage + 1 : null;
  const to = totalItems && itemsPerPage ? Math.min(currentPage * itemsPerPage, totalItems) : null;

  return (
    <div className="flex flex-col items-center gap-2 py-4">
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          aria-label={tAuto("auto.components_explore_pagination.1", "Página anterior")}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        {pages.map((p, i) =>
          p === "..." ? (
            <span key={`gap-${i}`} className="px-2 text-muted-foreground">…</span>
          ) : (
            <Button
              key={p}
              variant={p === currentPage ? "default" : "ghost"}
              size="sm"
              onClick={() => onPageChange(p)}
              className="min-w-[36px]"
            >
              {p}
            </Button>
          )
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          aria-label={tAuto("auto.components_explore_pagination.2", "Próxima página")}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
      {from && to && totalItems && (
        <p className="text-xs text-muted-foreground">
          {from}–{to} de {totalItems}
        </p>
      )}
    </div>
  );
}

export default Pagination;
